import * as Sentry from "@sentry/browser";

const KNOWN_SENTRY_ERRORS = [
  "Failed to fetch",
  "The operation couldn’t be completed. Software caused connection abort",
  "cancelled",
  "cancelado",
  "The request timed out.",
  "The Internet connection appears to be offline.",
  "The network connection was lost.",
];

let SENTRY_SAMPLE_RATE = 0.2;
let LOW_PRIORITY_SENTRY_SAMPLE_RATE = 0.05;
// NOTE(di): For non production env, we would like to capture all exceptions which will help with debugging.
if (__NODE_ENV__ !== "production") {
  SENTRY_SAMPLE_RATE = 1.0;
  LOW_PRIORITY_SENTRY_SAMPLE_RATE = 1.0;
}

class SentryHelper {
  constructor(userId) {
    // Initialize the Sentry client.
    Sentry.init({
      dsn: __SENTRY_PROJECT_DSN_JS__,
      // From tips and tricks: https://docs.sentry.io/clients/javascript/tips/
      // Only send 20% of errors.
      sampleRate: SENTRY_SAMPLE_RATE,
      // Disable default Sentry logging of unhandled rejection events.
      // From open issue around the new Sentry SDK: https://github.com/getsentry/sentry-javascript/issues/2019
      integrations: [
        new Sentry.Integrations.GlobalHandlers({
          onunhandledrejection: false,
        }),
      ],
      environment: __NODE_ENV__,
      debug: __NODE_ENV__ !== "production",
      whitelistUrls: [
        /noom\.com/,
        ...(__NODE_ENV__ !== "production"
          ? [
              /buyflow-lambda\.(dev|test)\.wsli\.dev/,
              /buyflow-web-assets\.test\.wsli\.dev/,
            ]
          : []),
      ],
    });

    // Manually log unhandled promise rejection events in Sentry with better error messages,
    // instead of the default Sentry logging for unhandled promise rejections.
    window.onunhandledrejection = event => {
      if (event && typeof (event.preventDefault === "function")) {
        event.preventDefault();
        this.captureException(event.reason);
      }
    };

    if (userId) {
      _setSentryUserId(userId);
    }
  }

  captureException(e, fingerprint) {
    // Sample (do not log all) low priority known sentry errors.
    if (
      e.message &&
      KNOWN_SENTRY_ERRORS.includes(e.message) &&
      Math.random() > LOW_PRIORITY_SENTRY_SAMPLE_RATE
    ) {
      return;
    }

    Sentry.withScope(scope => {
      if (fingerprint) scope.setFingerprint(fingerprint);
      if (e.error) scope.setExtra("error", e.error);
      Sentry.captureException(e);
    });
  }

  captureMessageWithExtras(message, extras) {
    Sentry.withScope(scope => {
      for (const key in extras) {
        scope.setExtra(key, extras[key]);
      }
      Sentry.captureMessage(message);
    });
  }
}

/**
 * Update sentry and set the user_id once we get serverContext.
 * @param {object} userId
 */
function _setSentryUserId(userId) {
  Sentry.configureScope(scope => {
    scope.setUser({
      id: userId,
    });
  });
}

export default SentryHelper;
